import {Injectable} from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from '@angular/common/http';
import {BehaviorSubject, catchError, filter, finalize, Observable, switchMap, take, throwError} from 'rxjs';
import {AuthService} from 'app/core/auth/auth.service';
import {AuthToken} from '../../api/responses/Auth/auth-token';
import {AccountService} from "@portal/api/account.service";
import {Success} from "@portal/api/responses/api-response";
import {StorageService} from "@portal/shared/services/storage.service";
import {PartySelectedService} from "@portal/shared/services/party-selected.service";
import {Router} from "@angular/router";
import { OrganizationType } from '@portal/api/enums/organization-type';

const REFRESH_FAILED = 'failed';
type RefreshState = AuthToken | typeof REFRESH_FAILED | null;

// Espelha ErrorCode.TokenExpired do backend (Contracts/DTO/ErrorCode.cs)
const TOKEN_EXPIRED_ERROR_CODE = 7;

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    refreshTokenInProgress = false;
    refreshTokenSubject = new BehaviorSubject<RefreshState>(null);

    constructor(private authService: AuthService,
                private router: Router,
                private _accountService: AccountService,
                private _partySelectedService: PartySelectedService,
                private _storageService: StorageService) {

    }

    // https://itnext.io/angular-tutorial-implement-refresh-token-with-httpinterceptor-bfa27b966f57
    // https://github.com/melcor76/interceptors/blob/master/src/app/interceptors/auth.interceptor.ts
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        request = this.addAuthenticationToken(request);
        const requestToIgnore = request.url.includes('RefreshAccessToken') || request.url.includes('Login');

        if (this.authService.isAuthenticated && !requestToIgnore && this.authService.isTokenExpired()) {
            return this.refreshTokenInProgress
                ? this.setRequestsOnHold(request, next)
                : this.doRefreshToken(request, next);
        }

        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                // Login e RefreshAccessToken tratam os seus próprios erros: a página de login
                // mostra o erro do login; doRefreshToken decide o que fazer com o erro do refresh.
                if (requestToIgnore || error.status !== HttpStatusCode.Unauthorized) {
                    return throwError(() => error);
                }

                return this.refreshTokenInProgress
                    ? this.setRequestsOnHold(request, next)
                    : this.doRefreshToken(request, next);
            }));
    }

    private doRefreshToken(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        this.refreshTokenInProgress = true;

        // A null, os pedidos que chegarem entretanto ficam em espera até o token estar renovado
        this.refreshTokenSubject.next(null);

        const refreshToken = this.authService.refreshToken;

        if (!refreshToken) {
            return this.failRefresh(new HttpErrorResponse({status: HttpStatusCode.Unauthorized, statusText: 'No refresh token'}))
                .pipe(finalize(() => this.refreshTokenInProgress = false));
        }

        return this._accountService.getNewAccessToken(refreshToken).pipe(
            // Só o erro do próprio refresh chega aqui; o reenvio do pedido original é tratado pelo chamador
            catchError((err: HttpErrorResponse) => this.failRefresh(err)),
            switchMap((response: Success<AuthToken>) => {
                if (!response?.data) {
                    return this.failRefresh(new HttpErrorResponse({status: HttpStatusCode.Unauthorized, statusText: 'Refresh returned no token'}));
                }

                this.authService.authToken = response.data;
                this.authService.setIsLoggedIn(true);
                this.refreshTokenSubject.next(this.authService.getAuth());

                return next.handle(this.addAuthenticationToken(request));
            }),
            finalize(() => this.refreshTokenInProgress = false)
        );
    }

    private failRefresh(err: HttpErrorResponse): Observable<never> {
        const sessionInvalid = err.status === HttpStatusCode.BadRequest
            || err.status === HttpStatusCode.Unauthorized
            || err.error?.code === TOKEN_EXPIRED_ERROR_CODE;

        if (sessionInvalid) {
            this.authService.signOut();
            this.router.navigate(['sign-in'], {queryParams: {redirectURL: this.router.url}});
        }

        // Liberta os pedidos em espera com erro, em vez de os deixar pendurados
        this.refreshTokenSubject.next(REFRESH_FAILED);

        return throwError(() => err);
    }

    private setRequestsOnHold(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Espera até refreshTokenSubject ter um valor: o token novo, ou a indicação de que o refresh falhou
        return this.refreshTokenSubject
            .pipe(
                filter(state => state !== null),
                take(1),
                switchMap(state => state === REFRESH_FAILED
                    ? throwError(() => new HttpErrorResponse({status: HttpStatusCode.Unauthorized, statusText: 'Session refresh failed'}))
                    : next.handle(this.addAuthenticationToken(request))));
    }

    private addAuthenticationToken(request: HttpRequest<any>): HttpRequest<any> {
        const token = this.authService.accessToken;

        // If access token is null this means that user is not logged in
        if (!token) {
            return request;
        }

        // Sem Content-Type explícito (ex.: FormData) não se define nenhum: o browser põe o multipart com boundary.
        const contentType = request.headers.get('Content-Type');
        const authHeader = this.getHeaders(contentType);

        return authHeader
            ? request.clone({setHeaders: authHeader})
            : request;
    }

    getHeaders(contentType: string | null): any {
        const headers: any = contentType ? {'Content-Type': contentType} : {};
        const accessToken = this.authService.accessToken;
        if (accessToken) {
            headers.Authorization = 'Bearer ' + accessToken;
            headers['X-Client'] = 'portal';

            const partyId = this._partySelectedService.getPartyId() ?? this._storageService.getParty()?.id;
            if (partyId) {
                headers.PartyId = partyId.toString();
            }

            // storageService is required here becouse
            // organizationsSelectorService will throw cyclic
            // injection in some cenarios
            if (this._storageService.getOrg()?.id) {
                headers.orgId = this._storageService.getOrg().id;

                if (this._storageService.getOrg().type === OrganizationType.Association) {
                    const organizationYear = this._storageService.getOrganizationYear(this._storageService.getOrg().id);
                    if (organizationYear) {
                        headers.OrgYear = organizationYear.toString();
                    }
                }
            }
        }
        return headers;
    }

}
